import {
  Component,
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  on,
} from 'solid-js';
import { t } from '@trimble/i18n';
import { EditFileDetails, ImportedFileDetails } from './interfaces';
import { createStore } from 'solid-js/store';
import { Spinner } from '../spinner/Spinner';
import { formatBytes } from '@trimble/utilities';
import {
  toMedDateTimeString,
  type SurveyedDateTimeChangeResult,
} from '@trimble/date-utils';
import { OptionalTierLock } from '@trimble/tiering';
import { TieringConfig } from '@trimble/types';
import { TextDisplay } from '../text-display/TextDisplay';
import { SurveyedDateTimeFields } from '../surveyed-date-time-fields/SurveyedDateTimeFields';

export interface ImportedFileDetailsPanelProps {
  editTieringOptions?: TieringConfig;
  details: ImportedFileDetails;
  onFileDetailClose?(): void;
  onModeChange?(hasBackdrop: boolean): void;
}

export const ImportedFileDetailsPanel: Component<
  ImportedFileDetailsPanelProps
> = (props) => {
  const [mode, setMode] = createSignal<'View' | 'Edit' | 'Save'>('View');

  const resetEditDetail = createMemo((): EditFileDetails => {
    const newDetails = props.details;
    return {
      importedFileUid: newDetails.importedFileUid,
      bgImageId: newDetails.bgImageId,
      errors: newDetails.errors,
      surveyedDate: newDetails.surveyedDate,
      surveyedDateTemp: undefined,
      surveyedTimeTemp: undefined,
      dateFeedback: undefined,
      timeFeedback: undefined,
      handleFileDetailEdit: newDetails.handleFileDetailEdit,
      name: newDetails.name,
      fileTypeDisplay: newDetails.fileTypeDisplay,
      fileCreatedDate: newDetails.fileCreatedDate,
      fileUpdatedDate: newDetails.fileUpdatedDate,
      importedBy: newDetails.importedBy,
      importedDate: newDetails.importedDate,
      source: newDetails.source,
      version: newDetails.version,
      size: newDetails.size,
    };
  });

  const [editDetail, setEditDetail] =
    // eslint-disable-next-line solid/reactivity
    createStore<EditFileDetails>(resetEditDetail());

  createEffect(
    on(
      resetEditDetail,
      (newDetails) => {
        setMode('View');
        setEditDetail(newDetails);
      },
      { defer: true }
    )
  );

  createEffect(() => {
    if (mode() === 'Edit' || mode() === 'Save') {
      props.onModeChange?.(true);
    } else {
      props.onModeChange?.(false);
    }
  });

  /**
   * Updates surveyed date state from the shared date/time fields.
   */
  const handleSurveyedDateChange = (
    result: SurveyedDateTimeChangeResult
  ): void => {
    setEditDetail({
      surveyedDate: result.surveyedDate ?? null,
      surveyedDateTemp: result.dateString,
      surveyedTimeTemp: result.timeString,
      dateFeedback: result.dateFeedback,
      timeFeedback: result.timeFeedback,
    });
  };

  const handleEditCancel = (): void => {
    setEditDetail({ ...props.details });
    setMode('View');
  };

  const handleEditSave = async (): Promise<void> => {
    setMode('Save');
    const savedDetail = await props.details.handleFileDetailEdit?.(editDetail);

    if (!savedDetail) {
      setEditDetail(props.details);
      setMode('View');
      return;
    }

    setEditDetail({ ...savedDetail });
    if (savedDetail.errors.length > 0) {
      setMode('Edit');
    } else {
      setMode('View');
    }
  };

  return (
    <div class="flex flex-col h-full">
      <div
        class="py-1 px-4 flex-1 overflow-y-auto"
        data-testid="imported-file-details-panel-details-container"
      >
        <div class="flex items-center justify-between pt-2 pb-1">
          <modus-wc-typography size="md" hierarchy="p" weight="semibold">
            <div
              data-testid="imported-file-details-panel-detail-heading"
              class="text-sm"
            >
              {t('shared.importedFileDetailsPanel.details')}
            </div>
          </modus-wc-typography>
          <Show when={mode() === 'View' && editDetail.handleFileDetailEdit}>
            <OptionalTierLock options={props.editTieringOptions}>
              <modus-button
                class="ms-2"
                data-testid="edit-imported-file-details"
                color="secondary"
                button-style="borderless"
                icon-only="pencil"
                size="small"
                onClick={() => setMode('Edit')}
              />
            </OptionalTierLock>
          </Show>
        </div>
        <For each={editDetail.errors}>
          {(item) => (
            <div class="pb-2" data-testid={`file-details-error-${item}`}>
              <modus-alert
                type="error"
                message={t('shared.importedFileDetailsPanel.errors.' + item)}
              />
            </div>
          )}
        </For>

        <section class="flex flex-col gap-3">
          <TextDisplay
            label={t('shared.importedFileDetailsPanel.importedFileType')}
            detail={editDetail.fileTypeDisplay}
          />
          <Switch>
            <Match when={mode() === 'View'}>
              <TextDisplay
                label={t('shared.importedFileDetailsPanel.surveyedDate')}
                detail={toMedDateTimeString(editDetail.surveyedDate)}
              />
            </Match>
            <Match when={mode() === 'Edit' || mode() === 'Save'}>
              <SurveyedDateTimeFields
                disabled={mode() === 'Save'}
                surveyedDate={editDetail.surveyedDate ?? undefined}
                dateString={editDetail.surveyedDateTemp}
                timeString={editDetail.surveyedTimeTemp}
                dateFeedback={editDetail.dateFeedback}
                timeFeedback={editDetail.timeFeedback}
                futureDateTimeMessage={t(
                  'shared.importedFileDetailsPanel.feedback.futureDate'
                )}
                onChange={handleSurveyedDateChange}
              />
            </Match>
          </Switch>
          <TextDisplay
            label={t('shared.importedFileDetailsPanel.importedDate')}
            detail={toMedDateTimeString(editDetail.importedDate)}
          />
          <TextDisplay
            label={t('shared.importedFileDetailsPanel.importedBy')}
            detail={editDetail.importedBy ?? undefined}
          />
          <TextDisplay
            label={t('shared.importedFileDetailsPanel.importedFrom')}
            detail={editDetail.source}
          />

          <TextDisplay
            label={t('shared.importedFileDetailsPanel.version')}
            detail={editDetail.version?.toString()}
          />
          <TextDisplay
            label={t('shared.importedFileDetailsPanel.size')}
            detail={
              editDetail.size != undefined
                ? formatBytes(editDetail.size)
                : undefined
            }
          />
        </section>
      </div>

      <Show when={mode() === 'Edit' || mode() === 'Save'}>
        <div class="flex-shrink-0">
          <div class="p-4 border-t border-gray-3">
            <div class="grid grid-cols-2 gap-3">
              <modus-wc-button
                data-testid="cancel-edit-btn"
                aria-label="cancel"
                color="primary"
                size="md"
                variant="outlined"
                attr:full-width={true}
                disabled={mode() === 'Save'}
                onClick={() => {
                  handleEditCancel();
                }}
              >
                <modus-wc-icon name="close" size="xs" />
                {t('shared.importedFileDetailsPanel.cancelBtn')}
              </modus-wc-button>
              <modus-wc-button
                data-testid="save-edit-btn"
                aria-label="save"
                color="primary"
                size="md"
                variant="filled"
                attr:full-width={true}
                disabled={!editDetail.surveyedDate || mode() === 'Save'}
                onClick={() => void handleEditSave()}
              >
                <Show
                  when={mode() === 'Save'}
                  fallback={
                    <modus-wc-icon name="check_circle_outlined" size="xs" />
                  }
                >
                  <Spinner size={16} color="#0063a3" />
                </Show>
                {t('shared.importedFileDetailsPanel.saveBtn')}
              </modus-wc-button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
